
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../context/ThemeContext';
import { ViewState, Role } from '../../types';
import { Logo } from '../ui/Logo';
import { GhostBrandIcon } from '../ui/PartnerIcons';
import { navigationConfig } from '../../constants';

interface SidebarProps {
  currentView: ViewState;
  onNavigate: (view: ViewState) => void;
  currentRole: Role;
  isDemoMode: boolean;
  activeRole: Role;
  isSidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  isMobileMenuOpen: boolean;
  onCloseMobileMenu: () => void;
}

const NavItem: React.FC<{ view: ViewState, icon: any, label: string, currentView: ViewState, onNavigate: (view: ViewState) => void, isCollapsed: boolean }> = ({ view, icon: Icon, label, currentView, onNavigate, isCollapsed }) => (
    <div className="relative group">
        <button
          onClick={() => onNavigate(view)}
          className={`w-full flex items-center gap-3 rounded-lg transition-all duration-200 font-bold uppercase tracking-wider ${isCollapsed ? 'justify-center p-3' : 'px-4 py-3'} ${
            currentView === view 
              ? 'bg-stone-100 dark:bg-[#2D3142] text-[#0A0B0F] dark:text-white shadow-sm border border-stone-200 dark:border-white/5' 
              : 'text-stone-500 dark:text-gray-400 hover:text-[#0A0B0F] dark:hover:text-white hover:bg-stone-50 dark:hover:bg-white/5'
          }`}
        >
          <Icon size={18} className={currentView === view ? "text-nobel-gold" : "text-stone-400 dark:text-gray-500 group-hover:text-[#0A0B0F] dark:group-hover:text-white"} />
          <span className={isCollapsed ? 'hidden' : 'text-[11px]'}>{label}</span>
        </button>
        {isCollapsed && (
            <div className="absolute left-full top-1/2 -translate-y-1/2 ml-4 px-3 py-1.5 bg-[#0A0B0F] dark:bg-[#2D3142] text-white text-xs font-bold rounded-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none shadow-lg z-[100]">
                {label}
            </div>
        )}
    </div>
);

export const Sidebar: React.FC<SidebarProps> = ({ 
    currentView, 
    onNavigate, 
    currentRole, 
    isDemoMode, 
    activeRole, 
    isSidebarCollapsed, 
    onToggleSidebar,
    isMobileMenuOpen,
    onCloseMobileMenu
}) => {
    const { user } = useAuth();
    const { theme } = useTheme();
    
    const filteredNav = navigationConfig.filter(item => item.roles.includes(currentRole));
    const sections: Record<string, typeof navigationConfig> = {};
    filteredNav.forEach(item => {
        if (!sections[item.section]) sections[item.section] = [];
        sections[item.section].push(item);
    });

    return (
        <>
            {/* Mobile Backdrop */}
            {isMobileMenuOpen && (
                <div 
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[45] md:hidden"
                    onClick={onCloseMobileMenu}
                />
            )}

            <aside 
                className={`
                    fixed inset-y-0 left-0 z-[50] h-full bg-white dark:bg-[#0A0B0F] border-r border-stone-200 dark:border-[#1a1a1a] flex flex-col transition-transform duration-300 ease-in-out
                    ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
                    ${isSidebarCollapsed ? 'md:w-20' : 'md:w-72 w-[85%] max-w-xs'}
                `}
            >
                {/* Desktop Collapse Toggle */}
                <button 
                    onClick={onToggleSidebar}
                    className="hidden md:flex absolute -right-3.5 top-10 w-7 h-7 bg-white dark:bg-[#0A0B0F] border border-stone-200 dark:border-[#1a1a1a] text-stone-500 hover:text-nobel-gold hover:border-nobel-gold/50 rounded-full items-center justify-center shadow-xl transition-all z-50 group"
                    aria-label={isSidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
                >
                    {isSidebarCollapsed ? <ChevronRight size={14} className="group-hover:scale-110 transition-transform" /> : <ChevronLeft size={14} className="group-hover:scale-110 transition-transform" />}
                </button>

                {/* Mobile Close Button */}
                <button 
                    onClick={onCloseMobileMenu}
                    className="md:hidden absolute top-4 right-4 p-2 text-stone-400 hover:text-stone-900 dark:hover:text-white"
                >
                    <X size={20} />
                </button>

                <div className={`h-20 flex items-center transition-all duration-300 ${isSidebarCollapsed ? 'justify-center' : 'px-8'}`}>
                    {isSidebarCollapsed ? (
                        <div className="w-10 h-10 flex items-center justify-center transition-transform hover:scale-110">
                            <GhostBrandIcon className="w-full h-full" />
                        </div>
                    ) : (
                        <Logo variant={theme === 'light' ? 'light' : 'dark'} type="os" />
                    )}
                </div>

                {isDemoMode && !isSidebarCollapsed && (
                    <div className="px-6 py-2 bg-stone-50 dark:bg-white/5 mx-4 rounded-lg border border-stone-100 dark:border-white/5">
                        <div className="text-[10px] text-stone-500 dark:text-gray-400 uppercase tracking-widest text-center">
                            DEMO MODE: <span className="text-[#0A0B0F] dark:text-white font-bold">{activeRole}</span>
                        </div>
                    </div>
                )}

                <nav className={`flex-1 overflow-y-auto custom-scrollbar transition-all duration-300 ${isSidebarCollapsed ? 'p-3' : 'p-6 space-y-8'}`}>
                    {Object.entries(sections).map(([section, items]) => (
                        <div key={section}>
                            <div className={`px-4 py-2 text-[9px] font-black text-stone-400 dark:text-stone-600 uppercase tracking-[0.25em] mb-1.5 transition-all duration-300 ${isSidebarCollapsed ? 'hidden' : 'text-left'}`}>
                                {section}
                            </div>
                            <div className="space-y-1">
                                {items.map(item => (
                                    <NavItem key={item.view} {...item} currentView={currentView} onNavigate={onNavigate} isCollapsed={isSidebarCollapsed} />
                                ))}
                            </div>
                        </div>
                    ))}
                </nav>

                <div className={`border-t border-stone-100 dark:border-white/5 bg-stone-50 dark:bg-[#0F1014] transition-all duration-300 ${isSidebarCollapsed ? 'p-3' : 'p-6'}`}>
                    <div className={`flex items-center gap-4 ${isSidebarCollapsed ? 'justify-center' : ''}`}>
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-tr from-stone-200 to-stone-400 dark:from-gray-700 dark:to-gray-900 flex items-center justify-center text-sm font-bold text-stone-700 dark:text-white shadow-inner border border-stone-200 dark:border-white/10 shrink-0">
                            {user?.name.charAt(0).toUpperCase()}
                        </div>
                        {!isSidebarCollapsed && (
                            <div className="flex-1 overflow-hidden">
                                <div className="text-sm font-bold text-[#0A0B0F] dark:text-white truncate">{user?.name}</div>
                                <div className="text-[10px] text-stone-500 uppercase font-bold truncate tracking-wider">{user?.role} Access</div>
                            </div>
                        )}
                    </div>
                </div>
            </aside>
        </>
    );
};
